import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useMerchandiseTopBrands from 'hooks/Merchandise/useMerchandiseTopBrands'
import useMerchandiseTopCategories from 'hooks/Merchandise/useMerchandiseTopCategories'
import useRecentlyViewedMerchandise from 'hooks/Merchandise/useRecentlyViewedMerchandise'
import useMerchandiseList from 'hooks/Merchandise/useMerchandiseList'
import { take } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import MerchandiseSearchFormResultItem from './MerchandiseSearchFormResultItem'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'

const ResultsGrid = styled.div`
  display: grid;

  ${mediaQueryUp.largeDesktop} {
    grid-template-columns: repeat(3, 1fr);
    gap: ${rem(40)}
  }
`

function MerchandiseSearchFormPlaceholderResults() {
  const intl = useIntl()
  const [brands] = useMerchandiseTopBrands()
  const [categories] = useMerchandiseTopCategories()
  const [recentlyViewed] = useRecentlyViewedMerchandise()

  const topProductsFilters: App.MerchandiseFilters = useMemo(() => ({
    sortBy: 'recommended',
  }), [])

  const [topProducts] = useMerchandiseList(topProductsFilters)

  const trackingProductConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form Placeholder results', 'product'), { key: 'product' },
  )
  const trackingCategoryConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form Placeholder results', 'category'), { key: 'category' },
  )
  const trackingBrandConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form Placeholder results', 'brand'), { key: 'brand' },
  )

  const hasRecentlyViewed = !!recentlyViewed.length
  const productsToDisplay = hasRecentlyViewed ? recentlyViewed : topProducts.map(product => product.id)
  const productsSectionTitle = hasRecentlyViewed
    ? intl.formatMessage({
        id: 'jfc3xpwTEI',
        defaultMessage: 'Recently viewed',
        description: 'Section title for recently viewed merchandise products',
      })
    : intl.formatMessage({
        id: '62YePcH0wo',
        defaultMessage: 'Shop by product',
        description: 'Section title for shopping by product in the merchandise shop section',
      })

  return <VerticalSpacer gap={24}>
    {productsToDisplay.length > 0 && <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">{productsSectionTitle}</Caption>
      <ResultsGrid>
        <OfferListEventsProvider tracking={trackingProductConfig}>
          {take(productsToDisplay, 3).map((merchandiseId, index) => (
            <MerchandiseSearchFormResultItem
              key={merchandiseId}
              itemId={merchandiseId}
              type="product"
              index={index}
            />
          ))}
        </OfferListEventsProvider>
      </ResultsGrid>
    </VerticalSpacer>}
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">
        {intl.formatMessage({
          id: 'muxLQtJxPH',
          defaultMessage: 'Shop by category',
          description: 'Section title for shopping by category in the merchandise shop section',
        })}
      </Caption>
      <ResultsGrid>
        <OfferListEventsProvider tracking={trackingCategoryConfig}>
          {take(categories, 3).map((category, index) => (
            <MerchandiseSearchFormResultItem
              key={category.id}
              itemId={category.id}
              type="category"
              index={index}
            />
          ))}
        </OfferListEventsProvider>
      </ResultsGrid>
    </VerticalSpacer>
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">
        {intl.formatMessage({
          id: '+CbegOdg9x',
          defaultMessage: 'Shop by brand',
          description: 'Section title for shopping by brand in the merchandise shop section',
        })}
      </Caption>
      <ResultsGrid>
        <OfferListEventsProvider tracking={trackingBrandConfig}>
          {take(brands, 3).map((brand, index) => (
            <MerchandiseSearchFormResultItem
              key={brand.id}
              itemId={brand.id}
              type="brand"
              index={index}
            />
          ))}
        </OfferListEventsProvider>
      </ResultsGrid>
    </VerticalSpacer>
  </VerticalSpacer>
}

export default MerchandiseSearchFormPlaceholderResults
